import React from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, Button, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Rnd } from 'react-rnd';

interface DocumentViewerModalProps {
  open: boolean;
  onClose: () => void;
  /** Full URL to the document, e.g. /api/attachments/files/... */
  url: string;
  /** Display title shown in the title bar */
  title?: string;
  /** MIME type hint – used to decide whether to iframe-preview */
  mimeType?: string;
}

const DEFAULT_W = 720;
const DEFAULT_H = 860;

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  open,
  onClose,
  url,
  title = 'Document',
  mimeType,
}) => {
  if (!open) return null;

  const isPDF =
    !mimeType ||
    mimeType === 'application/pdf' ||
    url.toLowerCase().includes('.pdf');
  const isImage =
    mimeType?.startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|svg)$/i.test(url);
  const canPreview = isPDF || isImage;

  // Suppress the left nav panel and fit the whole page on open
  const iframeSrc = isPDF ? `${url}#navpanes=0&zoom=page-fit` : url;

  // Center on first open
  const initialX = Math.max(0, (window.innerWidth - DEFAULT_W) / 2);
  const initialY = Math.max(0, (window.innerHeight - DEFAULT_H) / 2);

  return createPortal(
    // Backdrop – click outside to close
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        backgroundColor: 'rgba(0,0,0,0.45)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Rnd
        default={{ x: initialX, y: initialY, width: DEFAULT_W, height: DEFAULT_H }}
        minWidth={380}
        minHeight={280}
        bounds="window"
        dragHandleClassName="doc-viewer-title-bar"
        style={{ zIndex: 1301 }}
        enableResizing={{
          top: true, right: true, bottom: true, left: true,
          topRight: true, bottomRight: true, bottomLeft: true, topLeft: true,
        }}
      >
        <Paper
          elevation={24}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
          }}
        >
          {/* ── Title bar (drag handle) ── */}
          <Box
            className="doc-viewer-title-bar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              cursor: 'move',
              flexShrink: 0,
              userSelect: 'none',
              bgcolor: 'background.paper',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <AttachFileIcon fontSize="small" color="action" />
              <Typography
                variant="subtitle1"
                component="span"
                noWrap
                title={title}
                sx={{ fontWeight: 600 }}
              >
                {title}
              </Typography>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              gap={1}
              flexShrink={0}
              ml={2}
              // Prevent button clicks from triggering drag
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in New Tab
              </Button>
              <IconButton onClick={onClose} size="small" aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* ── Content ── */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {canPreview ? (
              <iframe
                src={iframeSrc}
                title={title}
                style={{ flex: 1, border: 'none', width: '100%', height: '100%' }}
              />
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                flex={1}
                gap={2}
                p={4}
              >
                <AttachFileIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography color="text.secondary">
                  Preview is not available for this file type.
                </Typography>
                <Button
                  variant="contained"
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download File
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Rnd>
    </Box>,
    document.body,
  );
};

export default DocumentViewerModal;
